import * as Checkbox from '@radix-ui/react-checkbox'
import { CheckIcon } from '@radix-ui/react-icons'
import {
  useEffect,
  useMemo,
} from 'react'
import {
  SubmitHandler,
  useForm,
} from 'react-hook-form'
import toast from 'react-hot-toast'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import {
  UpdateNotificationSettingsRequest,
  useReadNotificationSettingsQuery,
  useUpdateNotificationSettingsMutation,
} from '@/redux/notification'

type SettingKey =
  | 'followUpdate'
  | 'updatesFromFollowee'
  | 'competitionUpdate'
  | 'botUpdate';

export default function Notification() {
  const { data: notificationSettings } = useReadNotificationSettingsQuery()
  const [updateNotificationSettings] = useUpdateNotificationSettingsMutation()

  const settings = useMemo(() => {
    const result = [
      {
        key: 'followUpdate' as SettingKey,
        title: 'Follow Update',
        descriptions: ['Enabled notifications for all followed accounts.'],
        checked: notificationSettings?.followUpdate,
        onCheckedChange: (checked: boolean) => {
          updateNotificationSettings({
            followUpdate: checked === true,
          })
        },
      },
      {
        key: 'updatesFromFollowee' as SettingKey,
        title: 'Updates from followee',
        descriptions: [
          'A followed user published a new AI bot.',
          'A followed user updated an AI bot.',
          'A followed user applied for a new event.',
        ],
        checked: notificationSettings?.updatesFromFollowee,
        onCheckedChange: (checked: boolean) => {
          updateNotificationSettings({
            updatesFromFollowee: checked === true,
          })
        },
      },
      {
        key: 'competitionUpdate' as SettingKey,
        title: 'Event update (for all users)',
        descriptions: [
          'A new event was published by AxGrad.',
          'Event is now open for applications.',
          'Event competition has started or ended.',
        ],
        checked: notificationSettings?.competitionUpdate,
        onCheckedChange: (checked: boolean) => {
          updateNotificationSettings({
            competitionUpdate: checked === true,
          })
        },
      },
      {
        key: 'botUpdate' as SettingKey,
        title: 'AI bot updates',
        descriptions: [
          'A purchased AI bot has been updated or delete.',
          'Your AI bot was sold: 10 / 50 / 100 / 1K copies milestone reached.',
          'Your AI bot received a new rating from another user.',
        ],
        checked: notificationSettings?.botUpdate,
        onCheckedChange: (checked: boolean) => {
          updateNotificationSettings({
            botUpdate: checked === true,
          })
        },
      },
    ]
    return result
  }, [notificationSettings, updateNotificationSettings])

  const {
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { isSubmitting },
  } = useForm<UpdateNotificationSettingsRequest>()

  useEffect(() => {
    if (notificationSettings) {
      console.log('notificationSettings:', notificationSettings)
      reset({
        followUpdate: notificationSettings.followUpdate,
        updatesFromFollowee: notificationSettings.updatesFromFollowee,
        competitionUpdate: notificationSettings.competitionUpdate,
        botUpdate: notificationSettings.botUpdate,
      })
    }
  }, [notificationSettings, reset])

  const onSubmit: SubmitHandler<UpdateNotificationSettingsRequest> = async (
    values,
  ) => {
    await updateNotificationSettings({ ...values }).unwrap()
    reset()
    toast.success('Notification settings updated.')
  }

  return (
    <div>
      <div className="flex flex-col gap-12">
        {notificationSettings &&
          settings.map((setting, index) => (
            <div key={index}>
              <div className="flex items-center gap-3">
                <Checkbox.Root
                  checked={watch(setting.key) || false}
                  onCheckedChange={(checked) => {
                    setValue(setting.key, checked === true)
                  }}
                  className={cn(
                    'size-5 rounded-full flex items-center justify-center cursor-pointer',
                    watch(setting.key) ? 'bg-primary' : 'border border-primary',
                  )}
                  id={setting.key}
                >
                  <Checkbox.Indicator className="size-4">
                    <CheckIcon className="size-4" />
                  </Checkbox.Indicator>
                </Checkbox.Root>
                <div className="leading-4.5 text-lg font-semibold">
                  {setting.title}
                </div>
              </div>
              <div className="pl-8 mt-4 text-muted-foreground leading-6">
                <ul className="flex flex-col gap-1 list-disc pl-4">
                  {setting.descriptions.map((description: string) => (
                    <li key={description}>{description}</li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
      </div>
      <div className="mt-20">
        <Button
          className="w-40 h-12"
          variant="primary"
          onClick={handleSubmit(onSubmit)}
          isLoading={isSubmitting}
        >
          Save
        </Button>
      </div>
    </div>
  )
}
